import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import UserSummaryCard from "../../components/Profile/UserSummaryCard";
import WalletSummaryCard from "../../components/Profile/WalletSummaryCard";
import WalletStatsCard from "../../components/Profile/WalletStatsCard";
import DataTable from "../../components/Elements/DataTable/DataTable";
import EditUserModal from "../../components/Profile/EditUserModal";
import RefundModal from "../../components/Profile/RefundModal";
import TwoFactorSettingsModal from "../../components/Profile/TwoFactorSettingsModal";
import TopUpStatusModal from "../../components/Profile/TopUpStatusModal/TopUpStatusModal";

import WalletService from "../../services/WalletService";
import { fetchWalletTransactions } from "../../services/TransactionsFetchers";
import { fetchInvoicePdf } from "../../services/TransactionService";
import { downloadBlob, parseFilename } from "../../utils/downloadFile";
import MyAccountService from "../../services/MyAccountService";
import { AuthContext } from "../../context/AuthContext";
import { useGeo } from "../../context/GeoContext";
import { trackAnalyticsEvent } from "../../analytics/analytics";
import { useTranslation } from "react-i18next";

import "./Profile.css";

const PENDING_WALLET_TOPUP_PROVIDER_KEY = "pending_wallet_topup_provider";

const Profile = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const geoContext = useGeo() || {};
  const { geoCountryCode, geoReady, ensureGeoLoaded } = geoContext;

  const { token, userId, loading: authLoading, setToken, twoFactorMethod, walletCurrency } = useContext(AuthContext);

  const [topUpStatus, setTopUpStatus] = useState(null);
  const [isPayPalTopUp, setIsPayPalTopUp] = useState(false);

  useEffect(() => {
    ensureGeoLoaded?.();
  }, [ensureGeoLoaded]);

  useEffect(() => {
    const topUpSuccess = searchParams.get("success") === "true";
    const topUpCanceled = searchParams.get("canceled") === "true";

    if (topUpSuccess || topUpCanceled) {
      let paymentProvider = null;
      try {
        paymentProvider = localStorage.getItem(PENDING_WALLET_TOPUP_PROVIDER_KEY);
        localStorage.removeItem(PENDING_WALLET_TOPUP_PROVIDER_KEY);
      } catch (storageError) {
        console.error("Failed to read wallet top-up payment provider:", storageError);
      }
      setIsPayPalTopUp(topUpSuccess && paymentProvider === "paypal");
    }

    if (topUpSuccess) {
      setTopUpStatus("success");
    } else if (topUpCanceled) {
      setTopUpStatus("canceled");
    }
  }, [searchParams]);

  const handleCloseTopUpStatusModal = () => {
    setTopUpStatus(null);
    setIsPayPalTopUp(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("success");
    url.searchParams.delete("canceled");
    window.history.replaceState({}, "", url.toString());
  };

  const txColumns = useMemo(
    () => [
      { key: "name", label: t("transactions.columns.name", "Name"), width: "240px" },
      { key: "date", label: t("transactions.columns.date", "Date"), width: "120px" },
      { key: "amount", label: t("transactions.columns.amount", "Amount"), width: "120px" },
      { key: "transaction_type", label: t("transactions.columns.type", "Type"), width: "120px" },
      { key: "success", label: t("transactions.columns.status", "Status"), width: "160px" },
    ],
    [t],
  );

  const txOptionalColumns = useMemo(() => [], []);

  const txSortOptions = useMemo(
    () => [
      { label: t("transactions.columns.date", "Date"), value: "date" },
      { label: t("transactions.columns.amount", "Amount"), value: "amount" },
    ],
    [t],
  );

  const txFilters = useMemo(
    () => [
      {
        key: "transaction_type",
        type: "select",
        options: [
          { value: "service", label: t("transactions.types.service", "Service") },
          { value: "topup", label: t("transactions.types.topup", "Top-Up") },
          { value: "transfer", label: t("transactions.types.transfer", "Transfer") },
        ],
      },
    ],
    [t],
  );

  const [userData, setUserData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [walletStats, setWalletStats] = useState(null);

  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState("");

  const [isTwoFactorOpen, setIsTwoFactorOpen] = useState(false);
  const topUpTrackedRef = useRef(false);

  useEffect(() => {
    const isSuccess = searchParams.get("success") === "true";
    if (isSuccess && !topUpTrackedRef.current) {
      try {
        const rawTopUp = localStorage.getItem("pending_wallet_topup");
        if (rawTopUp) {
          topUpTrackedRef.current = true;
          const topUpData = JSON.parse(rawTopUp);
          trackAnalyticsEvent.walletTopUp(topUpData);
          localStorage.removeItem("pending_wallet_topup");
        }
      } catch (err) {
        console.error("Failed to parse or track wallet top-up:", err);
      }
    }
  }, [searchParams]);

  const getTableActions = useCallback(
    (item) => {
      const hasPaymentProvider = item.payment_provider && item.payment_provider !== "";
      const hasSuccessPayment = item.payment_status === "succeeded";

      if (hasPaymentProvider && hasSuccessPayment) {
        return [
          {
            label: t("transactions.actions.download", "Download"),
            onClick: async (transaction) => {
              try {
                const { blob, contentDisposition } = await fetchInvoicePdf(transaction.id);
                const filename = parseFilename(contentDisposition) || `invoice-${transaction.id}.pdf`;
                downloadBlob(blob, filename);
              } catch (e) {
                console.error("Invoice download failed:", e);
                setError(t("errors.failedToDownloadPdf", "Failed to download invoice PDF."));
              }
            },
          },
        ];
      }

      return [];
    },
    [t],
  );

  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;

    if (!token || !userId || userId === -1) {
      setLoadingUser(false);
      setLoadingWallet(false);
      setLoadingStats(false);
      return;
    }

    const fetchAll = async () => {
      try {
        setError("");

        setLoadingUser(true);
        const user = await MyAccountService.getUserData(userId, token);
        setUserData(user);
        setLoadingUser(false);

        setLoadingWallet(true);
        const wallet = await WalletService.getUserWallet(token);
        setWalletData(wallet);
        setLoadingWallet(false);

        if (wallet && wallet.id) {
          setLoadingStats(true);
          const stats = await WalletService.getWalletStats(wallet.id, token);
          setWalletStats(stats);
        }
      } catch (err) {
        console.error("Profile load error:", err);
        if (!error) {
          setError(err.message || t("errors.couldNotLoadWalletStats", "Could not load wallet statistics."));
        }
      } finally {
        setLoadingStats(false);
      }
    };

    fetchAll();
  }, [token, userId, authLoading, walletCurrency]);

  const isLoading = loadingUser || loadingWallet || loadingStats;
  const hasBasics = userData && walletData;

  const txFetchFn = useCallback(
    (params) =>
      fetchWalletTransactions({
        ...params,
        walletId: walletData?.id,
        language: i18n.language,
        lang: i18n.language,
      }),
    [walletData?.id, i18n.language],
  );

  const txRenderers = useMemo(
    () => ({
      amount: (tx) => `${tx.amount.toFixed(2)} ${tx.currency}`,
      date: (item) => {
        if (!item.date) return "-";

        const rawDate = item.date.endsWith("Z") ? item.date : `${item.date}Z`;
        const date = new Date(rawDate);

        const locale = i18n.language === "pl" ? "pl-PL" : i18n.language || "en-US";

        return date.toLocaleString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      transaction_type: (item) => {
        const types = {
          topup: t("transactions.types.topup", "Top-Up"),
          service: t("transactions.types.service", "Service"),
          transfer: t("transactions.types.transfer", "Transfer"),
          api: t("transactions.types.api", "API"),
          invoice: t("transactions.types.invoice", "Invoice"),
          refund: t("transactions.types.refund", "Refund"),
        };
        return types[item.transaction_type] || item.transaction_type || "-";
      },
      success: (item) => {
        const status = (item.payment_status || item.status || (item.success ? "succeeded" : "failed")).toLowerCase();
        const badgeClass = status === "succeeded" ? "success" : status === "pending" ? "pending" : "failed";

        return <span className={`status-badge ${badgeClass}`}>{t(`transactions.status.${status}`, status)}</span>;
      },
    }),
    [t, i18n.language],
  );

  const handleDeleteAccount = async () => {
    if (!userData || !token) return;

    const confirmed = window.confirm("Are you sure you want to delete your account? This action cannot be undone.");
    if (!confirmed) return;

    try {
      await MyAccountService.deleteUser(userData.email, token);

      setToken(null);
      localStorage.removeItem("token");

      navigate(`/${currentLang}/sign-in`);
    } catch (err) {
      setError(err.message || t("errors.couldNotDeleteAccount", "Could not delete your account."));
    }
  };

  const refreshProfileData = useCallback(async () => {
    if (!token || !userId || !walletData?.id) return;

    try {
      const wallet = await WalletService.getUserWallet(token);
      setWalletData((prev) => {
        if (!prev) return wallet;

        if (prev.id === wallet.id && prev.balance === wallet.balance && prev.currency === wallet.currency) {
          return prev;
        }

        return wallet;
      });

      if (wallet?.id) {
        const stats = await WalletService.getWalletStats(wallet.id, token);
        setWalletStats(stats);
      }
    } catch (err) {
      console.error("Refresh failed:", err);
      setError(t("errors.failedToRefreshData", "Failed to refresh data after transfer"));
    }
  }, [token, userId, walletData?.id]);

  return (
    <div className="profile-page">
      <PageHeader title={t("profile.title")} description={t("profile.subtitle")} />

      {error && <p className="info-text error-text">{error}</p>}
      {isLoading && !userData && !walletData && <p className="info-text">{t("loading")}</p>}

      {hasBasics && (
        <>
          <div className="profile-row">
            <UserSummaryCard
              user={{
                name: userData.username,
                email: userData.email,
                role: userData.role || "user",
              }}
              onEdit={() => setIsEditOpen(true)}
              onDelete={handleDeleteAccount}
              onManageTwoFactor={() => setIsTwoFactorOpen(true)}
              twoFactorMethod={twoFactorMethod}
            />

            <WalletSummaryCard
              wallet={walletData}
              userId={userId}
              token={token}
              onRefreshData={refreshProfileData}
              geoCountryCode={geoCountryCode}
              geoReady={geoReady}
            />

            <WalletStatsCard stats={walletStats} />
          </div>

          <EditUserModal
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            user={userData}
            token={token}
            onUpdated={(updated) => setUserData(updated)}
          />

          <RefundModal
            isOpen={refundModalOpen}
            onClose={() => setRefundModalOpen(false)}
            transactionId={selectedTransactionId}
            onSuccess={() => {
              setRefundModalOpen(false);
            }}
          />

          <TwoFactorSettingsModal isOpen={isTwoFactorOpen} onClose={() => setIsTwoFactorOpen(false)} />

          <div className="profile-transactions">
            <DataTable
              fetchDataFunction={txFetchFn}
              columns={txColumns}
              optionalColumns={txOptionalColumns}
              sortOptions={txSortOptions}
              filterDefinitions={txFilters}
              defaultSortBy="date"
              itemKey="id"
              customRenderers={txRenderers}
              rowActions={getTableActions}
              showCheckboxes={false}
              showFilters={false}
              showPageSize={false}
            />
          </div>
        </>
      )}

      <TopUpStatusModal
        isOpen={!!topUpStatus}
        type={topUpStatus}
        isPayPal={isPayPalTopUp}
        onClose={handleCloseTopUpStatusModal}
      />
    </div>
  );
};

export default Profile;
